import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../AxiosInstance";
import useReconnectingWebSocket from "../../../hooks/useReconnectingWebSocket";
import ProbeResult from "../../../components/network_quality/probe_result";
import styles from "../network_map.module.css";

const WAIT_SECONDS = 75;

/**
 * "Medir ahora": pide al equipo una medición por MQTT y muestra lo que llega
 * por WebSocket (la nube reenvía cada `network_sample` al grupo del equipo).
 */
export default function ProbeNow({ devices, initialPdui, onResults }) {
  const { t } = useTranslation();
  const [pdui, setPdui] = useState(initialPdui || "");
  const [kind, setKind] = useState("quick");
  const [status, setStatus] = useState(null);
  const [waiting, setWaiting] = useState(0);
  const [samples, setSamples] = useState([]);
  const requestedAt = useRef(null);
  const onResultsRef = useRef(onResults);
  onResultsRef.current = onResults;

  useEffect(() => {
    if (!pdui && devices && devices.length === 1) setPdui(String(devices[0].id));
  }, [devices, pdui]);

  useReconnectingWebSocket({
    path: pdui ? `/ws/client/pdui_${pdui}_netmap/` : null,
    enabled: Boolean(pdui),
    pduiId: pdui ? Number(pdui) : null,
    label: "WebSocket de cobertura",
    onMessage: (data) => {
      if (data.type !== "remote_mgmt_message" || data.topic !== "network_sample") return;
      if (!requestedAt.current) return;
      const sample = data.payload || {};
      setSamples((prev) => {
        const next = prev.filter((s) => s.interface !== sample.interface);
        return [...next, sample];
      });
      setStatus({ type: "ok", text: t("netmap.probe.results") });
      if (onResultsRef.current) onResultsRef.current();
    },
  });

  useEffect(() => {
    if (waiting <= 0) return undefined;
    const timer = setTimeout(() => {
      setWaiting((w) => {
        if (w <= 1) {
          setStatus((s) => (s && s.type === "ok" ? s : { type: "warn", text: t("netmap.probe.timeout") }));
          return 0;
        }
        return w - 1;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [waiting, t]);

  const send = async () => {
    if (!pdui) return;
    setSamples([]);
    requestedAt.current = Date.now();
    setStatus({ type: "info", text: t("netmap.probe.sent") });
    try {
      await axiosInstance.post(`pdui/${pdui}/network-probe/`, { kind });
      setWaiting(WAIT_SECONDS);
    } catch (err) {
      setStatus({ type: "error", text: err.response?.data?.error || t("netmap.probe.error") });
      setWaiting(0);
    }
  };

  const statusClass = status?.type === "warn" ? styles.statusWarn : status?.type === "error" ? styles.statusError : "";

  return (
    <div>
      <div className={styles.sectionTitle}>{t("netmap.probe.title")}</div>
      <div className={styles.sectionHelp}>{t("netmap.probe.help")}</div>
      <div className={styles.probeRow}>
        <select className={styles.select} value={pdui} onChange={(e) => setPdui(e.target.value)}>
          <option value="">{t("netmap.probe.choose")}</option>
          {(devices || []).map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
        </select>
        <div className={styles.chips}>
          <button type="button" className={`${styles.chip} ${kind === "quick" ? styles.chipActive : ""}`} onClick={() => setKind("quick")}>{t("netmap.probe.quick")}</button>
          <button type="button" className={`${styles.chip} ${kind === "full" ? styles.chipActive : ""}`} onClick={() => setKind("full")}>{t("netmap.probe.full")}</button>
        </div>
        <button type="button" className={styles.primaryButton} onClick={send} disabled={!pdui || waiting > 0}>
          {waiting > 0 ? t("netmap.probe.waiting", { s: waiting }) : t("netmap.probe.send")}
        </button>
      </div>
      {status && <div className={`${styles.status} ${statusClass}`}>{status.text}</div>}
      <ProbeResult samples={samples} compact />
    </div>
  );
}
